import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  OnboardingContextActionType,
  useOnboardingContext,
  useOnboardingDispatch
} from '@/contexts/onboardingContext';
import useMixpanel from '@/hooks/useTracking/useMixpanel';
import useTranslate from '@/hooks/useTranslate';
import { useLeadSources } from '@/pages/members/hooks/useLeadSources';
import { useReferrerCodeLookup } from '@/pages/members/hooks/useReferrerCodeLookup';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import { HowDidYouHearFormValues } from '@/pages/members/pages/Onboarding/steps/HowDidYouHearReferralStep/types';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import { StepProps } from '@/pages/members/pages/Onboarding/steps/types';
import getSubmittedData from '@/pages/members/pages/Onboarding/utils/getSubmittedData';
import Alert from '@/ui/members/components/Alert';
import FieldError from '@/ui/members/components/form/FieldError';
import RadioGroup from '@/ui/members/components/form/RadioGroup';
import Heading from '@/ui/members/components/Heading';
import InputGroup from '@/ui/members/components/InputGroup/InputGroup';
import Spinner from '@/ui/members/components/Spinner';

import ReferralOrganizationDropdown from './components/ReferralOrganizationDropdown';

const HowDidYouHearReferralStep = ({ nodeType, uid }: StepProps) => {
  const onboardingContext = useOnboardingContext();
  const dispatch = useOnboardingDispatch();
  const mixpanel = useMixpanel();
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.HowDidYouHearStep`);

  // State to track if we're validating the referrer code from the URL
  // Validation involves checking if the referrer code corresponds to a valid organization
  const [isValidatingReferrerCode, setIsValidatingReferrerCode] = useState(true);
  const [hasTriggeredSubmit, setHasTriggeredSubmit] = useState(false);

  const { data: leadSourcesResponse, isPending: isPendingLeadSources, isError } = useLeadSources();

  // Extract referrer_code from URL params
  const { initializedData } = onboardingContext;
  const { referrer_code } = initializedData?.urlParams || {};

  // Call the referrer code lookup endpoint to get entity id and type
  const {
    data: referrerCodeData,
    isLoading: isLoadingReferrerCode,
    isError: isReferrerCodeError
  } = useReferrerCodeLookup({
    code: referrer_code,
    enabled: !!referrer_code
  });

  const formattedLeadSources = useMemo(() => {
    return (
      leadSourcesResponse?.map((leadSource) => {
        return {
          label: leadSource.name,
          value: leadSource.id
        };
      }) || []
    );
  }, [leadSourcesResponse]);

  const {
    control,
    handleSubmit: defaultHandleSubmit,
    formState: { errors, isValid },
    watch,
    setValue
  } = useForm<HowDidYouHearFormValues>({
    defaultValues: getSubmittedData(nodeType, uid, onboardingContext.steps) || {
      referrerName: '',
      leadSourceId: '',
      referrerId: ''
    }
  });

  const values = watch();
  const selectedLabel = formattedLeadSources.find(
    (source) => source.value === values.leadSourceId
  )?.label;
  const isReferralSourceLead = /^my (?:case|doctor).+$/im.test(selectedLabel ?? '');

  useEffect(() => {
    // Clear referrerId and referrerName if the selected lead source is not a referral
    if (!isReferralSourceLead) {
      setValue('referrerId', '');
      setValue('referrerName', '');
    }

    // Clear referrerName if referrerId is not "other"
    if (values.referrerId !== 'other') {
      setValue('referrerName', '');
    }
  }, [isReferralSourceLead, setValue, values.referrerId, values.referrerName]);

  useEffect(() => {
    if (!referrer_code) {
      setIsValidatingReferrerCode(false);
      return;
    }

    // Check if we're still loading referrer code data
    if (isLoadingReferrerCode) {
      return;
    }

    // Check if the code lookup was successful
    if (!referrerCodeData || referrerCodeData.length === 0 || isReferrerCodeError) {
      setIsValidatingReferrerCode(false);
      return;
    }

    // Only dispatch once
    if (hasTriggeredSubmit) {
      return;
    }

    // Track that this step was bypassed
    mixpanel.trackOnboardingResolvedStep(nodeType, onboardingContext.version, 'bypassed');

    // Automatically trigger the submission
    // While we have step-skipping utilities available, they cannot be used on the final step,
    // so auto-submitting is the simpler approach here (works for both person and organization types)
    dispatch({
      type: OnboardingContextActionType.TRIGGER_SUBMIT,
      payload: {
        node: nodeType,
        uid: uid
      }
    });

    setHasTriggeredSubmit(true);
    setIsValidatingReferrerCode(false);
  }, [
    referrer_code,
    referrerCodeData,
    isReferrerCodeError,
    isLoadingReferrerCode,
    hasTriggeredSubmit,
    nodeType,
    uid,
    mixpanel,
    onboardingContext.version,
    dispatch
  ]);

  const handleSubmit = (callback: (data: HowDidYouHearFormValues) => void) => {
    // If we have a valid referrer code, auto-submit with prefilled data
    if (referrer_code && referrerCodeData && referrerCodeData.length > 0) {
      const referrer = referrerCodeData[0];

      const prefilledData: HowDidYouHearFormValues = {
        referrerId: referrer.entity_id,
        referrerName: '',
        leadSourceId: '',
        ...(referrer.entity_type.model === 'person' && { roleOfReferrer: 'Friend/Family' })
      };

      return () => callback(prefilledData);
    }

    return defaultHandleSubmit((data) => {
      if (data.referrerId === 'other') {
        // If "Other" is selected, clear referrerId to avoid sending "other" as an ID
        return callback({
          ...data,
          referrerId: ''
        });
      }
      callback(data);
    });
  };

  if (isValidatingReferrerCode || hasTriggeredSubmit) {
    return (
      <StepComponent uid={uid} nodeType={nodeType} handleSubmit={handleSubmit}>
        <Spinner className="flex justify-center items-center my-6" />
      </StepComponent>
    );
  }

  return (
    <StepComponent uid={uid} nodeType={nodeType} handleSubmit={handleSubmit} isValid={isValid}>
      {isError && (
        <Alert level="error">
          <p>{t('leadSourcesError')}</p>
        </Alert>
      )}

      <div className="text-center mb-6">
        <p className="text-base mb-3">{t('lastQuestion')}</p>

        <Heading className="mb-3" value={1}>
          {t('howDidYouHearHeading')}
        </Heading>
      </div>

      <InputGroup>
        <Controller
          name="leadSourceId"
          control={control}
          rules={{ required: t('leadSourceRequired') }}
          render={({ field }) => (
            <>
              {isPendingLeadSources ? (
                <Spinner />
              ) : (
                <RadioGroup
                  autoFocus
                  extraDetailsInput={
                    <ReferralOrganizationDropdown
                      t={t}
                      control={control}
                      isReferralSourceLead={isReferralSourceLead}
                      values={values}
                    />
                  }
                  id="source-name"
                  onChange={field.onChange}
                  options={formattedLeadSources}
                  optionsClassName="grid gap-2 mb-6"
                  selectedValue={field.value}
                />
              )}
            </>
          )}
        />

        <FieldError error={errors.leadSourceId} />
      </InputGroup>
    </StepComponent>
  );
};

export default HowDidYouHearReferralStep;
